import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Trophy,
  Gift,
  Clock,
  Star,
  Users,
  Crown,
  ArrowRight,
  Gamepad2,
  Calendar,
  Timer,
  Flag,
  ChevronDown,
  ChevronUp,
  Lock,
  CheckCircle,
  Play,
  HelpCircle,
  X,
  Lightbulb,
  AlertCircle,
  FileText
} from 'lucide-react';
import api from '../../lib/axios';

const MONTHS = [
  { num: 1, name: 'Jan', fullName: 'Janvier' },
  { num: 2, name: 'Fév', fullName: 'Février' },
  { num: 3, name: 'Mar', fullName: 'Mars' },
  { num: 4, name: 'Avr', fullName: 'Avril' },
  { num: 5, name: 'Mai', fullName: 'Mai' },
  { num: 6, name: 'Juin', fullName: 'Juin' },
  { num: 7, name: 'Juil', fullName: 'Juillet' },
  { num: 8, name: 'Août', fullName: 'Août' },
  { num: 9, name: 'Sep', fullName: 'Septembre' },
  { num: 10, name: 'Oct', fullName: 'Octobre' },
  { num: 11, name: 'Nov', fullName: 'Novembre' },
  { num: 12, name: 'Déc', fullName: 'Décembre' }
];

// Hint Popup Modal Component
const HintPopupModal = ({ show, monthNum, hint, onClose }) => {
  if (!show || !monthNum) return null;
  
  const monthInfo = MONTHS.find(m => m.num === monthNum);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20 relative animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">
            Indice de {monthInfo?.fullName}
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Caractère mystère #{monthNum}
          </p>
        </div>
        
        {hint?.hint ? (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex-shrink-0 rounded-full bg-purple-500/20 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-purple-200 font-medium">{hint.hint}</p>
                {hint.hint_detail && (
                  <p className="text-sm text-purple-300/70 mt-2">{hint.hint_detail}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4 mb-4 text-center">
            <p className="text-slate-400">Aucun indice disponible pour ce mois.</p>
            <p className="text-xs text-slate-500 mt-1">Suivez-nous sur les réseaux sociaux pour les indices !</p>
          </div>
        )}
        
        <div className="text-center">
          <p className="text-xs text-slate-500">
            💡 Les indices sont publiés sur nos réseaux sociaux
          </p>
          <Button 
            onClick={onClose}
            className="mt-4 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
          >
            Compris !
          </Button>
        </div>
      </div>
    </div>
  );
};

const GamesListPage = () => {
  const [games, setGames] = useState([]);
  const [periodicGames, setPeriodicGames] = useState([]);
  const [finishedGames, setFinishedGames] = useState([]);
  const [mainGame, setMainGame] = useState(null);
  const [annualGame, setAnnualGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinished, setShowFinished] = useState(false);
  
  // Tab state: 'ponctuel' or 'coffre'
  const [activeTab, setActiveTab] = useState('coffre');
  
  // Hint popup state
  const [showHintPopup, setShowHintPopup] = useState(false);
  const [selectedHintMonth, setSelectedHintMonth] = useState(null);
  const [selectedHintData, setSelectedHintData] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const openHintPopup = (month, hint) => {
    setSelectedHintMonth(month);
    setSelectedHintData(hint);
    setShowHintPopup(true);
  };
  
  const closeHintPopup = () => {
    setShowHintPopup(false);
    setSelectedHintMonth(null);
    setSelectedHintData(null);
  };
  
  const getHintForMonth = (monthNum) => {
    if (!annualGame?.secret_hints) return null;
    return annualGame.secret_hints.find(h => h.month === monthNum);
  };

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const [gamesRes, mainRes, annualRes] = await Promise.all([
        api.get('/game/contests'),
        api.get('/game/main-game'),
        api.get(`/game/annual-game/${currentYear}`).catch(() => ({ data: null }))
      ]);
      
      // Set annual game data
      setAnnualGame(annualRes.data);
      
      // Separate games by type
      const allGames = gamesRes.data || [];
      const main = mainRes.data;
      
      // Periodic games (monthly) - get current year or latest year with periodic games
      let periodic = allGames.filter(g => g.game_type === 'periodique' && g.year === currentYear);
      
      // If no games for current year, check previous year
      if (periodic.length === 0) {
        const yearsWithPeriodic = [...new Set(allGames.filter(g => g.game_type === 'periodique').map(g => g.year))].sort((a, b) => b - a);
        if (yearsWithPeriodic.length > 0) {
          const latestYear = yearsWithPeriodic[0];
          periodic = allGames.filter(g => g.game_type === 'periodique' && g.year === latestYear);
        }
      }
      setPeriodicGames(periodic);
      
      // Active non-periodic games
      const activeNonPeriodic = allGames.filter(g => !g.is_finished && g.game_type !== 'periodique');
      const finishedList = allGames.filter(g => g.is_finished && g.game_type !== 'periodique');
      
      if (main) {
        setMainGame(main);
        setGames(activeNonPeriodic.filter(g => g.id !== main.id));
      } else {
        setGames(activeNonPeriodic);
      }
      setFinishedGames(finishedList);
    } catch (e) {
      console.error('Error fetching games:', e);
    } finally {
      setLoading(false);
    }
  };

  // Get status of a month in the annual timeline
  const getMonthStatus = (monthNum) => {
    const monthGame = periodicGames.find(g => g.month === monthNum);
    
    if (monthGame) {
      if (monthGame.is_finished) {
        return { status: 'finished', game: monthGame };
      } else if (monthGame.is_active) {
        return { status: 'active', game: monthGame };
      } else {
        // Game exists but not active yet (scheduled)
        return { status: 'scheduled', game: monthGame };
      }
    }
    
    // No game for this month
    if (monthNum < currentMonth) {
      return { status: 'passed', game: null }; // Month passed without game
    } else if (monthNum === currentMonth) {
      return { status: 'current_no_game', game: null }; // Current month, no game
    } else {
      return { status: 'upcoming', game: null }; // Future month
    }
  };

  // Format opening date
  const formatOpeningDate = (game) => {
    if (!game.start_date) return null;
    const date = new Date(game.start_date);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} à 00h01`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hint Popup Modal */}
      <HintPopupModal 
        show={showHintPopup}
        monthNum={selectedHintMonth}
        hint={selectedHintData}
        onClose={closeHintPopup}
      />
      
      {/* Hero with Tabs */}
      <section className="py-12 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2306b6d4%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-cyan-500/30">
              <Gamepad2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Jeux Ponctuels
            </h1>
            <p className="text-lg text-slate-400">
              Participez à nos jeux flash et tentez de gagner des lots !
            </p>
          </div>
          
          {/* Bannière Le Coffre MSC */}
          <Link 
            to="/le-coffre-msc"
            className="block max-w-2xl mx-auto mb-8 group"
          >
            <div className="bg-gradient-to-r from-red-900/80 to-black border-2 border-red-500/50 rounded-xl p-4 flex items-center gap-4 transition-all hover:border-red-500 hover:shadow-lg hover:shadow-red-500/20">
              <div className="w-14 h-14 rounded-xl bg-black/50 flex items-center justify-center flex-shrink-0 group-hover:animate-coffre-shake">
                <img src="/images/coffre-fort.png" alt="" className="w-10 h-10 object-contain" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold flex items-center gap-2">
                  🎭 Le Coffre MSC {currentYear}
                  <Badge className="bg-red-600 text-white text-xs">NOUVEAU</Badge>
                </h3>
                <p className="text-white/60 text-sm">
                  12 mois, 12 indices, 1 combinaison à découvrir • {annualGame?.prize || 'Gros lot à gagner'}
                </p>
              </div>
              <ArrowRight className="w-6 h-6 text-red-500 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>

      {/* ============================================ */}
      {/* JEUX PONCTUELS */}
      {/* ============================================ */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Main Game if exists */}
          {mainGame && (
            <div className="mb-8">
              <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50 overflow-hidden">
                <CardContent className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-xl">
                        <Crown className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <Badge className="bg-cyan-500 text-white mb-2">
                          <Star className="w-3 h-3 mr-1" /> Jeu Principal
                        </Badge>
                        <h2 className="text-2xl font-bold text-white mb-2">{mainGame.title}</h2>
                        <p className="text-slate-400 mb-4">{mainGame.description}</p>
                        <div className="flex flex-wrap gap-4 mb-4">
                          <span className="flex items-center gap-1 text-amber-400">
                            <Gift className="w-4 h-4" /> {mainGame.prize}
                          </span>
                          <span className="flex items-center gap-1 text-slate-400">
                            <Timer className="w-4 h-4" /> {mainGame.time_limit_seconds}s
                          </span>
                        </div>
                        <Link to={`/jeu/${mainGame.id}`}>
                          <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                            <Play className="w-4 h-4 mr-2" /> Jouer maintenant
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Other Games */}
            {games.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {games.map(game => (
                  <Card key={game.id} className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all group">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Gamepad2 className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{game.title}</h3>
                      <p className="text-slate-400 text-sm mb-4 line-clamp-2">{game.description || "Participez et tentez de gagner !"}</p>
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <span className="flex items-center gap-1 text-amber-400">
                          <Gift className="w-4 h-4" /> {game.prize}
                        </span>
                      </div>
                      <Link to={`/jeu/${game.id}`}>
                        <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700">
                          Jouer <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : !mainGame && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Gamepad2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">Aucun jeu ponctuel disponible pour le moment.</p>
                  <p className="text-slate-500 text-sm mt-2">Découvrez Le Coffre MSC, notre jeu annuel !</p>
                  <Button 
                    onClick={() => setActiveTab('coffre')} 
                    className="mt-4 bg-gradient-to-r from-amber-500 to-orange-600"
                  >
                    <Gift className="w-4 h-4 mr-2" /> Voir Le Coffre MSC
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Finished Games */}
            {finishedGames.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowFinished(!showFinished)}
                  className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
                >
                  {showFinished ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Jeux terminés ({finishedGames.length})
                </button>
                {showFinished && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                    {finishedGames.map(game => (
                      <Card key={game.id} className="bg-slate-800/30 border-slate-700">
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-slate-400">{game.title}</h4>
                          <p className="text-sm text-slate-500">{game.prize}</p>
                          <Badge className="mt-2 bg-slate-700 text-slate-400">Terminé</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} L&apos;Agence MSC - Jeux concours réservés aux professionnels
          </p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/le-coffre-msc" className="text-red-400 hover:underline text-sm">
              🎭 Le Coffre MSC
            </Link>
            <span className="text-slate-600">•</span>
            <Link to="/site" className="text-cyan-400 hover:underline text-sm">
              Retour au site
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GamesListPage;
